import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, Polyline, FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import L from 'leaflet';

// Note: You might need to install react-leaflet-draw if not present, but user manual changes imply standard leaflet usage.
// Assuming simple Circle rendering for now without advanced draw controls unless requested.
// Wait, the user wants "load map ... show geofance on map". 
// Traccar geofences are usually WKT (POLYGON or CIRCLE). 
// I'll stick to displaying them for now. Interaction: "click on geofence i need have popup".

// Fix for default Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const TYPE_COLORS = {
    'SPLIT': '#3b82f6',
    'STAGE': '#a855f7', // Purple
    'TRACK': '#ec4899', // Pink
    'START': '#10b981',
    'META': '#ef4444',
    'SPEED_CONTROL': '#f59e0b',
    'DEFAULT': '#6366f1'
};

const FitBounds = ({ geofences }) => {
    const map = useMap();

    useEffect(() => {
        if (geofences.length > 0) {
            const bounds = L.latLngBounds([]);
            let hasValid = false;

            geofences.forEach(gf => {
                if (gf.area && gf.area.startsWith('CIRCLE')) {
                    // Parse CIRCLE(lat lon, radius) or similar. Traccar usually: CIRCLE (lat lon, radius)
                    // Standard WKT doesn't support CIRCLE strictly, but Traccar does.
                    // Format: CIRCLE (lat lon, radius meters)
                    try {
                        const content = gf.area.match(/\(([^)]+)\)/)[1];
                        const [coords, radius] = content.split(', ');
                        const [lat, lon] = coords.trim().split(' ');
                        if (lat && lon) {
                            bounds.extend([parseFloat(lat), parseFloat(lon)]);
                            hasValid = true;
                        }
                    } catch (e) { }
                }
                // Add POLYGON support if needed
            });

            if (hasValid) {
                map.fitBounds(bounds, { padding: [50, 50] });
            }
        }
    }, [geofences, map]);

    return null;
}

const FlyToSelected = ({ selectedGeofence, geofences }) => {
    const map = useMap();
    useEffect(() => {
        if (selectedGeofence && selectedGeofence.area) {
            // Re-parse to get center
            // Duplicate logic, ideally should be shared or passed down
            // For now, quick parse
            try {
                if (selectedGeofence.area.startsWith('CIRCLE')) {
                    const match = selectedGeofence.area.match(/\(([^)]+)\)/);
                    if (match) {
                        const [coords] = match[1].split(', ');
                        const [lat, lon] = coords.trim().split(' ');
                        if (lat && lon) {
                            map.flyTo([parseFloat(lat), parseFloat(lon)], 16);
                        }
                    }
                } else if (selectedGeofence.area.startsWith('POLYGON')) {
                    // Start of polygon
                    const match = selectedGeofence.area.match(/\(\(([^)]+)\)\)/);
                    if (match) {
                        const firstPair = match[1].split(',')[0].trim();
                        const [lat, lon] = firstPair.split(/\s+/);
                        if (lat && lon) {
                            map.flyTo([parseFloat(lat), parseFloat(lon)], 16);
                        }
                    }
                } else if (selectedGeofence.area.startsWith('LINESTRING')) {
                    const match = selectedGeofence.area.match(/\(([^)]+)\)/);
                    if (match) {
                        const firstPair = match[1].split(',')[0].trim();
                        const [lat, lon] = firstPair.split(/\s+/);
                        if (lat && lon) {
                            map.flyTo([parseFloat(lat), parseFloat(lon)], 16);
                        }
                    }
                }
            } catch (e) {
                console.warn("FlyTo failed", e);
            }
        }
    }, [selectedGeofence, map]);
    return null;
}

const GeofenceMap = ({ geofences, selectedGeofence, onGeofenceClick, onMapClick, onGeofenceCreate }) => {

    // Helper to parse Traccar Area string
    const parseGeofenceArea = (areaString) => {
        if (!areaString) return null;

        // Traccar often uses: CIRCLE (lat lon, radius) OR CIRCLE (lat, lon, radius)
        if (areaString.startsWith('CIRCLE')) {
            try {
                const params = areaString
                    .replace('CIRCLE', '')
                    .replace(/[()]/g, '')
                    .split(/[, ]+/)
                    .filter(Boolean)
                    .map(Number);

                if (params.length >= 3) {
                    const [lat, lon, radius] = params;
                    if (!isNaN(lat) && !isNaN(lon) && !isNaN(radius)) {
                        return { type: 'circle', center: [lat, lon], radius };
                    }
                }
            } catch (e) {
                console.warn("Failed to parse circle:", areaString, e);
            }
        }

        // LINESTRING (lat lon, lat lon, ...)
        if (areaString.startsWith('LINESTRING')) {
            try {
                const content = areaString.match(/\(([^)]+)\)/);
                if (content && content[1]) {
                    const points = content[1].split(',').map(pair => {
                        const [lat, lon] = pair.trim().split(/\s+/).map(Number);
                        return [lat, lon];
                    });
                    if (points.length > 0) {
                        return { type: 'linestring', positions: points };
                    }
                }
            } catch (e) {
                console.warn("Failed to parse linestring:", areaString, e);
            }
        }

        // POLYGON ((lat lon, lat lon, ...))
        if (areaString.startsWith('POLYGON')) {
            try {
                const content = areaString.match(/\(\(([^)]+)\)\)/);
                if (content && content[1]) {
                    const points = content[1].split(',').map(pair => {
                        const [lat, lon] = pair.trim().split(/\s+/).map(Number);
                        return [lat, lon];
                    });
                    if (points.length > 0) {
                        return { type: 'polygon', positions: points };
                    }
                }
            } catch (e) {
                console.warn("Failed to parse polygon:", areaString, e);
            }
        }

        return null;
    };

    const handleCreated = (e) => {
        const { layerType, layer } = e;
        if (layerType === 'circle') {
            const { lat, lng } = layer.getLatLng();
            const radius = layer.getRadius();
            const area = `CIRCLE (${lat.toFixed(6)} ${lng.toFixed(6)}, ${radius.toFixed(2)})`;
            // Trigger parent
            if (onGeofenceCreate) onGeofenceCreate(area);
        } else if (layerType === 'polyline') {
            const latlngs = layer.getLatLngs();
            // Traccar WKT for Linestring: LINESTRING (lat lon, lat lon, ...)
            const points = latlngs.map(p => `${p.lat.toFixed(6)} ${p.lng.toFixed(6)}`).join(', ');
            const area = `LINESTRING (${points})`;
            if (onGeofenceCreate) onGeofenceCreate(area);
        } else if (layerType === 'polygon') {
            const latlngs = layer.getLatLngs()[0]; // Leaflet polygons are nested arrays
            const points = latlngs.map(p => `${p.lat.toFixed(6)} ${p.lng.toFixed(6)}`).join(', ');
            // Close the loop for WKT polygon? Traccar might need it closed or not. Usually ((p1, p2, ..., p1))
            // Checking parsing logic: `POLYGON ((...))`
            const first = latlngs[0];
            const closedPoints = `${points}, ${first.lat.toFixed(6)} ${first.lng.toFixed(6)}`;
            const area = `POLYGON ((${closedPoints}))`;
            if (onGeofenceCreate) onGeofenceCreate(area);
        }

        // Remove the drawn layer immediately as we will reload geofences or show modal
        // Actually, we should probably keep it until saved?
        // For now, let's just remove it to avoid duplications when saving refreshes map
        // But we can't easily remove it from here without ref causing issues.
        // Let's assume the map re-renders or we just leave it for now.
        // Better: remove it
        // layer.remove(); // This might cause issues with EditControl state
        e.layer.remove();
    };

    return (
        <MapContainer
            center={[52.0, 19.0]}
            zoom={6}
            maxZoom={18}
            className="admin-map"
            style={{ height: '100%', width: '100%' }}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FitBounds geofences={geofences} />
            <FlyToSelected selectedGeofence={selectedGeofence} geofences={geofences} />

            <FeatureGroup>
                <EditControl
                    position="topright"
                    onCreated={handleCreated}
                    draw={{
                        rectangle: false,
                        circlemarker: false,
                        marker: false,
                        polyline: true,
                        polygon: true,
                        circle: true
                    }}
                />
            </FeatureGroup>

            {/* Click handler for map background */}
            <FeatureGroup eventHandlers={{ click: (e) => onMapClick && onMapClick(e.latlng) }} />


            {geofences.map(gf => {
                const shape = parseGeofenceArea(gf.area);
                if (!shape) return null;

                // Determine color
                const type = gf.attributes?.pointType || 'DEFAULT';
                const color = TYPE_COLORS[type] || TYPE_COLORS['DEFAULT'];
                const isSelected = selectedGeofence?.id === gf.id;

                if (shape.type === 'circle') {
                    return (
                        <Circle
                            key={gf.id}
                            center={shape.center}
                            radius={shape.radius}
                            pathOptions={{
                                color: color,
                                fillColor: color,
                                fillOpacity: isSelected ? 0.6 : 0.2,
                                weight: isSelected ? 3 : 1
                            }}
                            eventHandlers={{
                                click: (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    onGeofenceClick(gf);
                                }
                            }}
                        />
                    );
                }

                if (shape.type === 'polygon') {
                    return (
                        <Polygon
                            key={gf.id}
                            positions={shape.positions}
                            pathOptions={{
                                color: color,
                                fillColor: color,
                                fillOpacity: isSelected ? 0.6 : 0.2,
                                weight: isSelected ? 3 : 1
                            }}
                            eventHandlers={{
                                click: (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    onGeofenceClick(gf);
                                }
                            }}
                        />
                    );
                }

                if (shape.type === 'linestring') {
                    return (
                        <Polyline
                            key={gf.id}
                            positions={shape.positions}
                            pathOptions={{
                                color: color,
                                weight: isSelected ? 5 : 3
                            }}
                            eventHandlers={{
                                click: (e) => {
                                    L.DomEvent.stopPropagation(e);
                                    onGeofenceClick(gf);
                                }
                            }}
                        />
                    );
                }
                return null;
            })}
        </MapContainer>
    );
};

export default GeofenceMap;
